import classNames from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCartPricing, useCartPricingError } from '../../../../contexts/CartPricingContext';
import { centsToDollarsNumber } from '../../../../lib/util';
import * as types from '../../../../types';
import styles from './Confirm.module.scss';
import { useLocale } from '../../../../contexts/LocaleContext';
import { TFunction } from 'i18next';
import PricesInCurrencyMessage from '../../../../components/PricesInCurrencyMessage';
import { formatHardwarePrice } from '../../../../lib/hardwareFirst';
import { isEnrolledInPositionDiscount } from '../../../../lib/positionDiscount';
import useCartHasOnlyFreeTrialBundles from '../../../../hooks/useCartHasOnlyFreeTrialBundles';
import useFreeTrialShippingPriceInCents from '../../../../hooks/useFreeTrialShippingPriceInCents';
import useRequiresShippingDetails from '../../../../hooks/useRequiresShippingDetails';
import { partnerFreeTrialInCart } from '../../../../lib/gwpFreeTrial';
import { RenewalDisclosure, RescueItemizedTotals } from '../../../../components/CartSummary/RescueTotals';
import { showRescuePdpRedesign } from '../../../RescueLanding/RescueBanner';

interface PricingSummaryProps {
  selectedShippingOption?: types.IShippingOption;
}

function displayShippingPrice(
  shippingPriceInCents: number | undefined,
  currencyFormatter: Intl.NumberFormat,
  t: TFunction,
) {
  if (!shippingPriceInCents) {
    return t('free');
  }
  return currencyFormatter.format(centsToDollarsNumber(shippingPriceInCents));
}

function getDiscountLabel(cartPricing: types.CartPricing, t: TFunction) {
  // Position discounts are recurring, not one-time
  if (isEnrolledInPositionDiscount(cartPricing)) {
    return t('multi_pet_discount');
  }
  return t('one_time_promo');
}

export default function PricingSummary({ selectedShippingOption }: PricingSummaryProps) {
  const { t } = useTranslation();
  const cartPricing = useCartPricing();
  const pricingError = useCartPricingError();
  const locale = useLocale();
  const { currencyFormatter } = locale;
  const onlyFreeTrials = useCartHasOnlyFreeTrialBundles();
  const freeTrialShippingInCents = useFreeTrialShippingPriceInCents();
  const includeFreeTrialShipping = onlyFreeTrials;
  // Digital-only carts (e.g. gift cards) don't ship, so don't show a shipping line.
  const requiresShippingDetails = useRequiresShippingDetails();
  const showShipping = requiresShippingDetails && !!selectedShippingOption;
  // a hardware-first cart's server subtotal already nets both hardware savings out of the unit
  // prices; the column restates the list subtotal and shows each saving as its own row so it adds up
  const hardwarePromoInCents = Object.values(cartPricing.hardwareDiscounts ?? {}).reduce(
    (sum, discount) => sum + discount.promoDiscountInCents,
    0,
  );
  const multiDeviceInCents = cartPricing.hardwareFirstMultiDeviceDiscountInCents ?? 0;
  // a partner free trial's gift (the Mini and its free months) isn't a server discount, since the plan's first bill is
  // only deferred; it's restated the same way so the shopper sees what they're saving
  const partnerFreeTrialGiftInCents = partnerFreeTrialInCart(cartPricing)?.giftInCents ?? 0;
  const subtotalInCents =
    cartPricing.subtotalInCents + hardwarePromoInCents + multiDeviceInCents + partnerFreeTrialGiftInCents;
  // the arm hides zero cents everywhere ($99, $5) and shows them otherwise ($69.30); control keeps $99.00
  const money = (cents: number) =>
    cartPricing.hardwareDiscounts
      ? formatHardwarePrice(cents, locale)
      : currencyFormatter.format(centsToDollarsNumber(cents));
  // an adoption order is itemized as the cart itemizes it - membership, the adoption promo, the
  // activation fee - instead of a subtotal that already counts the fee and a generic "one-time
  // promo" row, which together never showed where the one real charge came from
  const rescue = showRescuePdpRedesign();
  return (
    <div className={styles.cartSummary}>
      <PricesInCurrencyMessage />
      {rescue ? (
        <RescueItemizedTotals className={styles.rescueTotals} />
      ) : (
        <div className={styles.row}>
          <div>{t('subtotal')}</div>
          <div>{money(onlyFreeTrials ? Math.max(subtotalInCents, 0) : subtotalInCents)}</div>
        </div>
      )}
      {!rescue && !!cartPricing.appliedDiscountInCents && (
        <div className={classNames(styles.row, styles.cartSavings)}>
          <div>{getDiscountLabel(cartPricing, t)}</div>
          <div>
            {'-'}
            {money(cartPricing.appliedDiscountInCents)}
          </div>
        </div>
      )}
      {partnerFreeTrialGiftInCents > 0 && (
        <div className={classNames(styles.row, styles.cartSavings)}>
          <div>{getDiscountLabel(cartPricing, t)}</div>
          <div>
            {'-'}
            {money(partnerFreeTrialGiftInCents)}
          </div>
        </div>
      )}
      {hardwarePromoInCents > 0 && (
        <div className={classNames(styles.row, styles.cartSavings)}>
          <div>{t('hardware_first_promo_code_discount')}</div>
          <div>
            {'-'}
            {money(hardwarePromoInCents)}
          </div>
        </div>
      )}
      {multiDeviceInCents > 0 && (
        <div className={classNames(styles.row, styles.cartSavings)}>
          <div>{t('hardware_first_cart_multi_device_discount')}</div>
          <div>
            {'-'}
            {money(multiDeviceInCents)}
          </div>
        </div>
      )}
      <div className={styles.row}>
        <div>{t('tax')}</div>
        <div>{pricingError ? '—' : money(cartPricing.taxInCents ?? 0)}</div>
      </div>
      {showShipping && (
        <div className={styles.row}>
          <div>{t('shipping')}</div>
          <div>
            {displayShippingPrice(
              includeFreeTrialShipping && !cartPricing.shippingInCents
                ? freeTrialShippingInCents
                : cartPricing.shippingInCents,
              currencyFormatter,
              t,
            )}
          </div>
        </div>
      )}
      {!!cartPricing.appliedGiftCardAmountInCents && (
        <div className={classNames(styles.row, styles.cartSavings)}>
          <div>
            {t('gift_card')}
            {cartPricing.totalValueOfRedeemedGiftCardInCents && (
              <span>
                {t('amount_value', {
                  amount: money(cartPricing.totalValueOfRedeemedGiftCardInCents),
                })}
              </span>
            )}
          </div>
          <div>
            {'-'}
            {money(cartPricing.appliedGiftCardAmountInCents)}
          </div>
        </div>
      )}
      {!!cartPricing.appliedAccountBalanceInCents && (
        <div className={classNames(styles.row, styles.cartSavings)}>
          <div>{t('account_balance')}</div>
          <div>
            {'-'}
            {money(cartPricing.appliedAccountBalanceInCents)}
          </div>
        </div>
      )}
      {!!cartPricing.upgradeCreditAmountInCents && (
        <div className={classNames(styles.row, styles.cartSavings)}>
          <div>{t('credits')}</div>
          <div>
            {'-'}
            {money(cartPricing.upgradeCreditAmountInCents)}
          </div>
        </div>
      )}
      <div className={classNames(styles.row, styles.cartTotal)}>
        <div>{includeFreeTrialShipping ? t('due_today') : t('total')}</div>
        <div>
          {pricingError
            ? '—'
            : (() => {
                const shippingAddon =
                  includeFreeTrialShipping && !cartPricing.shippingInCents ? freeTrialShippingInCents : 0;
                const total = cartPricing.totalInCents + shippingAddon;
                return total === 0 ? t('free') : money(total);
              })()}
        </div>
      </div>
      {rescue && <RenewalDisclosure className={styles.rescueRenewalDisclosure} />}
    </div>
  );
}
